import {CSSProperties, ReactNode, useEffect, useLayoutEffect, useRef, useState} from 'react';
import './PopupMenuView.scss';
import {Events, EventType} from '../../events/Events';
import {escapeStack} from '../../utils/Accessibility';
import {noop} from '../../utils/Utils';
import {Breakpoint, getBreakpoint} from '../../utils/Breakpoint';
import {delay} from '../../utils/Timing';
import FocusTrap from '../../utils/FocusTrap';
import Icon, {IconSizes} from '../icon/Icon';
import {i18n} from '../../i18n/i18n';
import ListView from '../listview/ListView';
import ListViewGroup from '../listview/listviewgroup/ListViewGroup';
import {Coordinates} from '../../types/device.types';

/**
 * Reference to a function, since functions passed directly to useState are interpreted as a state updater functions,
 * which would each be needed to be wrapped in another function, which is tough to read.
 */
interface FunctionRef {
  fn: () => void;
}

export interface PopupMenuViewArgs {
  node: ReactNode;
  origin: Coordinates;
}

function PopupMenuView() {

  const defaultContent = null;
  const [content, setContent] = useState<ReactNode>(defaultContent);
  const contentRef = useRef(content);

  const defaultOrigin = null;
  const [origin, setOrigin] = useState<Coordinates | null>(defaultOrigin);
  const originRef = useRef(origin);

  const defaultRemoteFromEscapeStack = { fn: noop };
  const [removeFromEscapeStack, setRemoveFromEscapeStack] = useState<FunctionRef>(defaultRemoteFromEscapeStack);
  const removeFromEscapeStackRef = useRef(removeFromEscapeStack);

  const defaultAnimation = null;
  const [animation, setAnimation] = useState<string | null>(defaultAnimation);

  const [mobile, setMobile] = useState(false);

  const contentWrapperRef = useRef<HTMLDivElement>(null);

  // Keep references in sync with state

  useEffect(() => {
    removeFromEscapeStackRef.current = removeFromEscapeStack;
  }, [removeFromEscapeStack]);

  useEffect(() => {
    contentRef.current = content;
    originRef.current = origin;
  }, [content, origin]);

  // Initialise component

  useEffect(() => {
    const breakpointChangeSubscription = Events.subscribe(EventType.BREAKPOINT_CHANGED, () => {
      whenBreakpointChanged();
    });

    const popupOpenSubscription = Events.subscribe(EventType.POPUPMENU_OPEN, (args) => {
      whenPopupMenuOpen(args);
    });

    const popupCloseSubscription = Events.subscribe(EventType.POPUPMENU_CLOSE, () => {
      whenPopupMenuClose();
    });

    function onWindowResized() {
      Events.publish(EventType.POPUPMENU_CLOSE);
    }

    window.addEventListener('resize', onWindowResized);
    whenBreakpointChanged();

    return () => {
      breakpointChangeSubscription.unsubscribe();
      popupOpenSubscription.unsubscribe();
      popupCloseSubscription.unsubscribe();
      window.removeEventListener('resize', onWindowResized);
      whenPopupMenuClose();
    };
  }, []);

  useLayoutEffect(() => {
    let contentWrapper = contentWrapperRef.current;
    if (contentWrapper != null && origin != null) {
      if (contentWrapper.getBoundingClientRect().bottom > window.innerHeight * 0.9) {
        contentWrapper.classList.add('popupmenuview__content--origin-bottom-left');
      } else {
        contentWrapper.classList.add('popupmenuview__content--origin-top-left');
      }
    }
  });

  // Private implementation

  function whenBreakpointChanged() {
    setMobile(getBreakpoint() === Breakpoint.PHONE);
  }

  function whenPopupMenuOpen(args: PopupMenuViewArgs) {
    setContent(args.node);
    setOrigin(args.origin);
    setRemoveFromEscapeStack({
      fn: escapeStack.push(() => {
        Events.publish(EventType.POPUPMENU_CLOSE);
      })
    });

    setAnimation('animate-in');
    delay(200, () => {
      setAnimation(defaultAnimation);
    });
  }

  function whenPopupMenuClose() {
    removeFromEscapeStackRef.current.fn();
    setRemoveFromEscapeStack(defaultRemoteFromEscapeStack);

    setAnimation('animate-out');
    delay(200, () => {
      setAnimation(defaultAnimation);
      setContent(defaultContent);
      setOrigin(defaultOrigin);
    });
  }

  // Rendering

  if (!content) {
    return null;
  }

  const classNames = ['popupmenuview'];
  classNames.push(mobile ? 'popupmenuview--mobile' : 'popupmenuview--desktop');

  const contentWrapperClassNames = ['popupmenuview__content'];
  const backdropClassNames = ['popupmenuview__backdrop'];
  if (animation) {
    contentWrapperClassNames.push(`popupmenuview__content--${animation}`);
    backdropClassNames.push(`popupmenuview__backdrop--${animation}`);
  }

  const inlineStyles: CSSProperties = {};
  if (!mobile && origin != null) {
    inlineStyles.left = origin.x + 'px';
    inlineStyles.top = origin.y + 'px';
  }

  return <div className={classNames.join(' ')}>
    <div className={backdropClassNames.join(' ')}
         aria-hidden="true"
         onClick={() => Events.publish(EventType.POPUPMENU_CLOSE)}>
    </div>
    <FocusTrap>
      <div ref={contentWrapperRef}
           className={contentWrapperClassNames.join(' ')}
           style={inlineStyles}>
        <div className="popupmenuview__closebuttonwrapper">
          <button className="popupmenuview__closebutton"
                  aria-label={i18n().global.closeLabel}
                  title={i18n().global.closeLabel}
                  onClick={() => whenPopupMenuClose()}>
            <Icon category="fa-close"
                  size={IconSizes.MEDIUM}/>
          </button>
        </div>
        <ListView>
          <ListViewGroup flat={true}>
            {content}
          </ListViewGroup>
        </ListView>
      </div>
    </FocusTrap>
  </div>;
}

export default PopupMenuView;
